/**
 * Reads design tokens out of the stylesheet so the 3D scene and the DOM cannot
 * drift apart: there is exactly one definition of every colour (tokens.css).
 */
export type TokenName =
  | 'space-deep'
  | 'space-near'
  | 'star'
  | 'ocean-deep'
  | 'ocean-shallow'
  | 'atmosphere'
  | 'health-0'
  | 'health-1'
  | 'health-2'
  | 'health-3'
  | 'health-4'
  | 'health-unknown'
  | 'trace-vegetation'
  | 'trace-settlement'
  | 'trace-lights'
  | 'debris-open'
  | 'debris-done'
  | 'debris-date'
  | 'debris-alarm'
  | 'sat-foil'
  | 'sat-white'
  | 'sat-grey'
  | 'sat-black'
  | 'sat-beacon'
  | 'sat-alert'
  | 'prophet-light'
  | 'prophet-halo'
  | 'medal-bronze'
  | 'medal-silver'
  | 'medal-gold'
  | 'medal-bright'
  | 'medal-post'
  | 'ribbon-1'
  | 'ribbon-2'
  | 'ribbon-3'
  | 'ribbon-4'
  | 'ribbon-5'
  | 'ribbon-6'
  | 'strength-held'
  | 'strength-gold'
  | 'strength-gold-deep'
  | 'strength-gold-light'
  | 'strength-reset'
  | 'strength-stone';

export function readToken(name: TokenName, el: Element = document.documentElement): string {
  return getComputedStyle(el).getPropertyValue(`--${name}`).trim();
}

export const HEALTH_RAMP: TokenName[] = ['health-0', 'health-1', 'health-2', 'health-3', 'health-4'];

/** Maps a 0 (healthy) to 1 (critical) score onto the ramp, interpolating in CSS colour space. */
export function healthColor(score: number): string {
  const clamped = Math.min(1, Math.max(0, score));
  const scaled = clamped * (HEALTH_RAMP.length - 1);
  const lower = Math.floor(scaled);
  const upper = Math.min(HEALTH_RAMP.length - 1, lower + 1);
  const t = scaled - lower;
  const from = HEALTH_RAMP[lower];
  const to = HEALTH_RAMP[upper];
  if (!from || !to) return `var(--health-unknown)`;
  if (lower === upper) return `var(--${from})`;
  return `color-mix(in oklch, var(--${from}) ${Math.round((1 - t) * 100)}%, var(--${to}))`;
}

export type Theme = 'dark' | 'light';

export function setTheme(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
}

export function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

/**
 * Resolves any CSS colour — including the oklch() the tokens are written in —
 * to plain 0-255 RGB, by asking the browser to paint one pixel of it.
 *
 * getComputedStyle is not enough: browsers hand back `oklch(...)` untouched,
 * and three.js cannot parse that, which silently renders the whole world white.
 */
let paintCanvas: HTMLCanvasElement | null = null;

export function cssColorToRgb(color: string): [number, number, number] {
  if (typeof document === 'undefined') return [255, 255, 255];

  paintCanvas ??= document.createElement('canvas');
  paintCanvas.width = 1;
  paintCanvas.height = 1;

  const ctx = paintCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [255, 255, 255];

  ctx.clearRect(0, 0, 1, 1);
  ctx.fillStyle = '#ffffff';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);

  const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
  return [r ?? 255, g ?? 255, b ?? 255];
}

/** The same, as a hex string three.js accepts. */
export function cssColorToHex(color: string): string {
  const [r, g, b] = cssColorToRgb(color);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** A token's value, already resolved to something three.js can use. */
export function sceneColor(name: TokenName, fallback = '#ffffff'): string {
  const raw = readToken(name);
  return raw ? cssColorToHex(raw) : fallback;
}

interface Oklch {
  l: number;
  c: number;
  h: number;
}

function parseOklch(value: string): Oklch | null {
  const match = /oklch\(\s*([\d.]+)%?\s+([\d.]+)\s+([\d.]+)/i.exec(value);
  if (!match) return null;
  const l = Number(match[1]);
  return {
    l: value.includes('%') ? l / 100 : l,
    c: Number(match[2]),
    h: Number(match[3]),
  };
}

/**
 * A health score as a colour three.js can use.
 *
 * The CSS `color-mix(in oklch, var(--health-0) …)` that the DOM uses cannot be
 * resolved by a canvas — custom properties do not exist there, and the browser
 * silently hands back white, which renders the entire world blank. So the ramp
 * is interpolated here in OKLCH and emitted as a literal, which canvas does
 * understand. Both paths read the same tokens, so they cannot drift.
 */
export function sceneHealthColor(score: number): string {
  const clamped = Math.min(1, Math.max(0, score));
  const scaled = clamped * (HEALTH_RAMP.length - 1);
  const lower = Math.floor(scaled);
  const upper = Math.min(HEALTH_RAMP.length - 1, lower + 1);
  const t = scaled - lower;

  const from = parseOklch(readToken(HEALTH_RAMP[lower] as TokenName));
  const to = parseOklch(readToken(HEALTH_RAMP[upper] as TokenName));
  if (!from || !to) return cssColorToHex(readToken('health-unknown') || '#888888');

  // Hue takes the short way round, so a ramp never swings through the far side
  // of the wheel on its way from green to red.
  let deltaHue = to.h - from.h;
  if (deltaHue > 180) deltaHue -= 360;
  if (deltaHue < -180) deltaHue += 360;

  const mixed: Oklch = {
    l: from.l + (to.l - from.l) * t,
    c: from.c + (to.c - from.c) * t,
    h: from.h + deltaHue * t,
  };

  return cssColorToHex(`oklch(${mixed.l} ${mixed.c} ${mixed.h})`);
}
